// src/components/Layout.tsx
import React, { useEffect, useRef, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import Sidebar from "./Sidebar";
import { Bell, Wallet, X } from "lucide-react";
import { apiFetch } from "../utils/api";
import { useAdminNotifications, AdminNotification } from "../hooks/useAdminNotifications";

interface UserBar {
  name: string;
  role: string;
  balance: number;
}

/* ── notification type badge colours ── */
const typeColor: Record<string, string> = {
  crypto_deposit:    "bg-purple-500",
  inr_deposit_new:   "bg-blue-500",
  inr_deposit_status:"bg-indigo-500",
  withdrawal_new:    "bg-orange-500",
  withdrawal_status: "bg-yellow-500",
};

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  return `${h}h ago`;
}

/* ── Notification drawer ── */
const NotificationDrawer: React.FC<{
  notifications: AdminNotification[];
  onMarkAllRead: () => void;
  onMarkRead: (id: string, db_id?: number) => void;
  onClear: () => void;
  onClose: () => void;
}> = ({ notifications, onMarkAllRead, onMarkRead, onClear, onClose }) => (
  <div className="absolute right-0 top-12 w-96 bg-[#1A2033] border border-[#2D334A] rounded-xl shadow-2xl z-50 flex flex-col max-h-[80vh]">
    {/* header */}
    <div className="flex items-center justify-between px-4 py-3 border-b border-[#2D334A]">
      <span className="text-white font-semibold text-sm">Notifications</span>
      <div className="flex items-center gap-3">
        {notifications.some((n) => !n.read) && (
          <button
            onClick={onMarkAllRead}
            className="text-xs text-blue-400 hover:text-blue-300 transition"
          >
            Mark all read
          </button>
        )}
        {notifications.length > 0 && (
          <button
            onClick={onClear}
            className="text-xs text-gray-500 hover:text-gray-300 transition"
          >
            Clear all
          </button>
        )}
        <button onClick={onClose} className="text-gray-400 hover:text-white transition">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>

    {/* list */}
    <div className="overflow-y-auto flex-1">
      {notifications.length === 0 ? (
        <p className="text-gray-500 text-sm text-center py-10">No notifications</p>
      ) : (
        notifications.map((n) => (
          <div
            key={n.id}
            onClick={() => onMarkRead(n.id, n.db_id)}
            className={`px-4 py-3 border-b border-[#2D334A] cursor-pointer hover:bg-[#232A42] transition ${
              !n.read ? "bg-[#1E2640]" : ""
            }`}
          >
            <div className="flex items-start gap-3">
              <span
                className={`mt-1 inline-block w-2 h-2 rounded-full flex-shrink-0 ${
                  !n.read ? "bg-blue-400" : "bg-transparent"
                }`}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span
                    className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded text-white ${
                      typeColor[n.type] ?? "bg-gray-600"
                    }`}
                  >
                    {n.type.replace(/_/g, " ")}
                  </span>
                  <span className="text-gray-500 text-xs ml-auto flex-shrink-0">
                    {timeAgo(n.timestamp)}
                  </span>
                </div>
                <p className="text-white text-sm font-medium truncate">{n.title}</p>
                <p className="text-gray-400 text-xs truncate">{n.body}</p>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  </div>
);

const Layout = () => {
  const location               = useLocation();
  const [isSidebarOpen, setSB] = useState(true);
  const [isMobile, setMobile]  = useState(window.innerWidth < 1024);
  const [user, setUser]        = useState<UserBar>({ name: "", role: "", balance: 0 });
  const [drawerOpen, setDrawer] = useState(false);
  const drawerRef              = useRef<HTMLDivElement>(null);

  const { notifications, unreadCount, markAllRead, markRead, clearAll } =
    useAdminNotifications();

  /* ──────────────────────────────  handle resize  */
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 1024);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => { if (isMobile) setSB(false); }, [isMobile]);

  /* ──────────────────────────────  close drawer on outside click  */
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (drawerRef.current && !drawerRef.current.contains(e.target as Node)) {
        setDrawer(false);
      }
    };
    if (drawerOpen) document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [drawerOpen]);

  /* ──────────────────────────────  fetch /api/staff/:id  */
  const fetchMe = async () => {
    const id = localStorage.getItem("currentUserId");
    if (!id) return;

    const [info, roll] = await Promise.all([
      apiFetch<{ name: string; role: string }>(`/api/staff/${id}`),
      apiFetch<any>(`/api/staff/rollup/${id}`),
    ]);

    setUser({
      name   : info.name,
      role   : info.role,
      balance: Number(roll.data?.own_balance || 0),
    });
  };
  useEffect(() => { fetchMe(); }, []);
  useEffect(() => {
    window.addEventListener("balance-changed", fetchMe);
    return () => window.removeEventListener("balance-changed", fetchMe);
  }, []);

  /* ──────────────────────────────  helpers  */
  const pageTitle = (() => {
    const seg = location.pathname.split("/").filter(Boolean).pop() || "dashboard";
    return seg
      .replace(/-/g, " ")
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (s) => s.toUpperCase());
  })();

  /* ──────────────────────────────  render  */
  return (
    <div className="flex h-screen bg-[#0F1525] overflow-hidden">
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={() => setSB(!isSidebarOpen)} />

      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <header className="bg-[#1A2033] border-b border-[#2D334A] p-4 flex justify-between items-center sticky top-0 z-10">
          <div className="flex items-center">
            <button
              className="lg:hidden text-gray-300 mr-4"
              onClick={() => setSB(!isSidebarOpen)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none"
                   viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M4 6h16M4 12h16M4 18h16"/>
              </svg>
            </button>
            <h1 className="text-white text-xl font-medium">{pageTitle}</h1>
          </div>

          <div className="flex items-center space-x-4">
            {/* Balance */}
            <div className="flex items-center space-x-2 text-white bg-[#0F1525] px-3 py-1.5 rounded-lg">
              <Wallet className="w-5 h-5 text-yellow-400" />
              <span className="text-sm font-semibold">Wallet {user.balance.toFixed(2)}</span>
            </div>

            {/* Notification bell */}
            <div className="relative" ref={drawerRef}>
              <button
                onClick={() => setDrawer((o) => !o)}
                className="relative p-2 rounded-lg bg-[#0F1525] text-gray-300 hover:text-white hover:bg-[#232A42] transition"
                title="Notifications"
              >
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 rounded-full text-[10px] font-bold text-white flex items-center justify-center px-0.5">
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}
              </button>

              {drawerOpen && (
                <NotificationDrawer
                  notifications={notifications}
                  onMarkAllRead={markAllRead}
                  onMarkRead={markRead}
                  onClear={clearAll}
                  onClose={() => setDrawer(false)}
                />
              )}
            </div>

            {/* User pill */}
            <div className="flex items-center space-x-3">
              <div className="h-8 w-8 rounded-full bg-[#646ECD] flex items-center justify-center text-white">
                <span className="text-sm font-medium">
                  {user.name ? user.name.charAt(0).toUpperCase() : "?"}
                </span>
              </div>
              <div>
                <h3 className="text-white text-sm font-semibold">{user.name || "..."}</h3>
                <p className="text-gray-400 text-xs">{user.role}</p>
              </div>
            </div>
          </div>
        </header>

        {/* Main area */}
        <main className="flex-1 overflow-auto bg-[#161B2B]">
          <div className="container mx-auto max-w-12xl">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
